import { MeterType, RULEBOOK_EXPORT_SCHEMA_V1_DRAFT, } from "@next-platform/packages-ts-types";
/** Build an object dropping `undefined` values so normalize's defaults are not clobbered. */
const omitUndefined = (record) => {
    const cleaned = {};
    Object.entries(record).forEach(([key, value]) => {
        if (value !== undefined) {
            cleaned[key] = value;
        }
    });
    return cleaned;
};
const isBlank = (text) => !text || !text.trim();
const toExportLevel = (level) => omitUndefined({
    label: level.label,
    to: level.to,
    instr: isBlank(level.instruction) ? undefined : level.instruction,
    caption: isBlank(level.caption) ? undefined : level.caption,
    revealed: level.revealed,
    override: level.override,
});
const toExportAi = (ai) => {
    if (!ai || (!ai.enabled && !ai.rows.length)) {
        return undefined;
    }
    return {
        on: ai.enabled,
        rules: ai.rows.map((row) => ({
            id: row.id,
            d: row.impact,
            when: row.condition,
            ...(row.cooldown !== undefined && row.cooldown !== 0 ? { cooldown: row.cooldown } : {}),
        })),
        lo: ai.per_turn_min,
        hi: ai.per_turn_max,
        ...(typeof ai.negative_overrides_positive === "boolean" ? { negOverrides: ai.negative_overrides_positive } : {}),
        ...(ai.mode !== undefined ? { mode: ai.mode } : {}),
        ...(ai.extract_hint !== undefined ? { extractHint: ai.extract_hint } : {}),
    };
};
const meterCommon = (meter) => omitUndefined({
    id: meter.id,
    type: meter.type,
    label: meter.label,
    icon: meter.icon,
    color: meter.color,
    tracks: meter.tracks,
    visible: meter.visible,
    raises: meter.tells_users?.raises,
    lowers: meter.tells_users?.lowers,
});
const toBoundedExport = (meter) => ({
    ...meterCommon(meter),
    min: meter.min,
    max: meter.max,
    def: meter.default,
    decay: meter.decay,
    ...(meter.decay_every !== undefined && meter.decay_every > 1 ? { decay_every: meter.decay_every } : {}),
    keywords: meter.keywords,
    ai: toExportAi(meter.ai),
    bands: meter.levels.map(toExportLevel),
});
const toCounterExport = (meter) => ({
    ...meterCommon(meter),
    def: meter.default,
    unit: meter.unit,
    display: meter.display,
    perMsg: meter.per_message,
    spend: meter.spendable,
    decimals: meter.decimals,
    tellModel: meter.tell_model,
    keywords: meter.keywords,
    ai: toExportAi(meter.ai),
    bands: meter.levels.map(toExportLevel),
});
const toClockExport = (meter) => ({
    ...meterCommon(meter),
    mode: meter.mode,
    display: meter.display,
    start: meter.start,
    stepMin: meter.step_minutes,
    stepEvery: meter.step_every_messages,
    skips: meter.detect_skips,
    ...(meter.skip_mode !== undefined && meter.skip_mode !== "rules" ? { skipMode: meter.skip_mode } : {}),
    tellModel: meter.tell_model,
    ruleDims: meter.rule_dimensions,
    todBands: meter.tod_levels.map(toExportLevel),
    dayBands: meter.day_levels.map(toExportLevel),
});
const toDiceExport = (meter) => ({
    ...meterCommon(meter),
    sides: meter.sides,
    bands: meter.levels.map(toExportLevel),
});
const toExportMeter = (meter) => {
    if (meter.type === MeterType.DICE) {
        return toDiceExport(meter);
    }
    if (meter.type === MeterType.CLOCK) {
        return toClockExport(meter);
    }
    if (meter.type === MeterType.NUMBER) {
        return toCounterExport(meter);
    }
    return toBoundedExport(meter);
};
const toExportCondition = (condition) => {
    if (condition.op === "tod_is") {
        return { m: condition.meter_id, op: "tod", v: condition.level_index };
    }
    return { m: condition.meter_id, op: condition.op, v: condition.value };
};
const toExportRule = (rule) => omitUndefined({
    id: rule.id,
    conds: rule.conditions.map(toExportCondition),
    instr: rule.instruction,
    caption: isBlank(rule.caption) ? undefined : rule.caption,
});
/**
 * Serialize a canonical `RulebookDefinition` into the compact `rulebook/v1-draft`
 * envelope `normalizeRulebookExport` understands (canonical field names would be dropped).
 */
export default function toRulebookExportEnvelope(definition) {
    const rulebook = omitUndefined({
        id: definition.id,
        name: definition.name,
        description: definition.description,
        meters: definition.meters.map(toExportMeter),
        combined_rules: definition.combined_rules.map(toExportRule),
    });
    return {
        schema: RULEBOOK_EXPORT_SCHEMA_V1_DRAFT,
        exported_at: new Date().toISOString(),
        rulebook,
    };
}
